import { inject } from '@angular/core';
import type { RedirectFunction, Route, Routes } from '@angular/router';
import { permissionGuard } from '../../../../core/guards/permission.guard';
import { PermissionService } from '../../../../core/services/permission.service';
import { PerformanceContextStore } from './shared/state/performance-context.store';
import { PerformanceReferenceStore } from './shared/state/performance-reference.store';
import { firstSetupRoute } from './setup/performance-setup-sections';

// SP4a part 2 (D74, D76): the Overview replaces the old dashboard. The wrapper FILE
// `performance-dashboard-page.component.ts` stays until SP4c deletes the old workspace; nothing routes to it.
const overviewLoader = () =>
  import('./overview/performance-overview-page.component').then((m) => m.PerformanceOverviewPageComponent);
// SP4c Task 12 (D76): the old workspace is gone. `goals` now loads the real Goals page (Task 1-3, 7).
const goalsLoader = () => import('./goals/goals-page.component').then((m) => m.GoalsPageComponent);
// SP4b Task 14: the four routes below move off the old workspace onto their real SP4b pages.
const cyclesListLoader = () => import('./cycles/cycles-list-page.component').then((m) => m.CyclesListPageComponent);
const evaluationsListLoader = () =>
  import('./evaluations/evaluations-list-page.component').then((m) => m.EvaluationsListPageComponent);
const feedbackPageLoader = () => import('./feedback/feedback-page.component').then((m) => m.FeedbackPageComponent);
const improvementPlansPageLoader = () =>
  import('./improvement-plans/improvement-plans-page.component').then((m) => m.ImprovementPlansPageComponent);
// SP4b Task 4 (D65): the cycle detail. `cycles/**` is `cyclesManage` (F2), so the cycle read and its config are allowed.
const cycleDetailLoader = () =>
  import('./cycles/detail/cycle-detail-page.component').then((m) => m.CycleDetailPageComponent);
// WN-1357 SP5: the cycle workspace's tab routes (lazy, so the static pending-changes wrapper does not guard them twice).
const cycleWorkspaceRoutesLoader = () =>
  import('./cycles/workspace/cycle-workspace.routes').then((m) => m.CYCLE_WORKSPACE_ROUTES);
// WN-1423: one cycle's HR analytics, read-only, OUTSIDE the workspace's cyclesManage gate: `analytics/**` is `analyticsRead`
// (the gate the overview's analytics section had), so moving analytics off the overview locks no caller out.
const cycleAnalyticsPageLoader = () =>
  import('./overview/analytics/cycle-analytics-page.component').then((m) => m.CycleAnalyticsPageComponent);
// SP4b Task 6 (D68): the reviewer's own form. `reviews/**` is module-only (F2, D27), so a peer reviewer with no
// Performance permission reaches it; the Overview's pending links look for EXACTLY `reviews/:reviewerId`.
const reviewerFormLoader = () => import('./reviews/reviewer-form-page.component').then((m) => m.ReviewerFormPageComponent);
// SP4b Task 8 (D67): the evaluation page. `evaluations/**` is `evaluationsRead` (F2) — the by-id read's own gate; the
// Overview's pending links look for EXACTLY `evaluations/:evaluationId` (`performanceChildPaths`).
const evaluationLoader = () =>
  import('./evaluations/detail/evaluation-page.component').then((m) => m.EvaluationPageComponent);
const ratingScalesLoader = () =>
  import('./setup/rating-scales/performance-rating-scales-page.component').then((m) => m.PerformanceRatingScalesPageComponent);
// SP4c Task 4 (D71/D38/D46): the KPI library setup page.
const kpiLibraryLoader = () =>
  import('./setup/kpi-library/kpi-library-page.component').then((m) => m.KpiLibraryPageComponent);
// SP4c Task 5 (D71/D45): the KPI sets setup pages.
const kpiCategoriesLoader = () =>
  import('./setup/kpi-library/kpi-categories-page.component').then((m) => m.KpiCategoriesPageComponent);
const kpiSetsLoader = () =>
  import('./setup/kpi-sets/kpi-sets-page.component').then((m) => m.KpiSetsPageComponent);
const kpiSetDetailLoader = () =>
  import('./setup/kpi-sets/kpi-set-detail-page.component').then((m) => m.KpiSetDetailPageComponent);
const settingsLoader = () =>
  import('./setup/settings/performance-settings-page.component').then((m) => m.PerformanceSettingsPageComponent);
const accessLoader = () =>
  import('./setup/access/performance-access-page.component').then((m) => m.PerformanceAccessPageComponent);
// SP4a part 2 (D60/D69): the rebuilt list. The old wrapper file `performance-templates-page.component.ts` stays
// until SP4c deletes the old workspace (D76); nothing routes to it any more.
const templatesListLoader = () =>
  import('./setup/templates/performance-templates-page.component').then((m) => m.PerformanceTemplatesPageComponent);
// D60/D69: the template builder, the detail page every templates link opens (list title, created copy, New version).
const templateBuilderLoader = () =>
  import('./setup/templates/builder/template-builder-page.component').then((m) => m.TemplateBuilderPageComponent);

/** D60: `/performance/setup` opens the first setup page the user may use, else the unauthorized page. */
export const performanceSetupRedirect: RedirectFunction = () => {
  const permissions = inject(PermissionService);
  return firstSetupRoute((anyOf) => permissions.hasAny(anyOf)) ?? '/unauthorized';
};

/**
 * D61: every performance page is re-checked on in-area navigation too — the admin shell's `permissionGuard` runs
 * only when the shell itself is entered. Redirect routes are left alone: Angular rejects `canActivate` beside
 * `redirectTo`, and the redirect's target is guarded anyway.
 */
export function withPerformancePermissionGuard(routes: Routes): Routes {
  return routes.map((route): Route =>
    route.loadComponent || route.component
      ? { ...route, canActivate: [...(route.canActivate ?? []), permissionGuard] }
      : route,
  );
}

export const PERFORMANCE_ROUTE: Route = {
  path: 'performance',
  // D62: one instance per Performance visit, shared by every performance page; both reset when the user leaves.
  providers: [PerformanceContextStore, PerformanceReferenceStore],
  children: withPerformancePermissionGuard([
    { path: '', pathMatch: 'full', loadComponent: overviewLoader },
    { path: 'cycles', loadComponent: cyclesListLoader },
    // WN-1357 SP5: the cycle workspace. The page is the parent (header, actions, dialogs); the tabs are its children,
    // `cycles/:cycleId/<tab>`. `cycles/:cycleId` itself redirects to the Overview tab and keeps its fragment (ruling 1).
    { path: 'cycles/:cycleId', loadComponent: cycleDetailLoader, loadChildren: cycleWorkspaceRoutesLoader },
    { path: 'analytics/:cycleId', loadComponent: cycleAnalyticsPageLoader },
    // D60: the old bookmark. Redirects carry no guard (Angular); the target is guarded with the same group (F26).
    { path: 'templates', pathMatch: 'full', redirectTo: '/performance/setup/templates' },
    { path: 'goals', loadComponent: goalsLoader },
    { path: 'evaluations', loadComponent: evaluationsListLoader },
    { path: 'evaluations/:evaluationId', loadComponent: evaluationLoader },
    { path: 'feedback', loadComponent: feedbackPageLoader },
    { path: 'improvement-plans', loadComponent: improvementPlansPageLoader },
    { path: 'reviews/:reviewerId', loadComponent: reviewerFormLoader },
    { path: 'setup', pathMatch: 'full', redirectTo: performanceSetupRedirect },
    { path: 'setup/templates', loadComponent: templatesListLoader },
    // F26: `performanceAreaPermissions(['setup','templates',…])` answers `templatesManage`, so no new rule.
    { path: 'setup/templates/:templateId', loadComponent: templateBuilderLoader },
    { path: 'setup/rating-scales', loadComponent: ratingScalesLoader },
    // F26: `performanceAreaPermissions(['setup','kpi-library'])` answers `templatesManage`, so no new rule.
    { path: 'setup/kpi-library', loadComponent: kpiLibraryLoader },
    // WN-1216: KPI categories are a page of their own (they were a dialog on the library); same permission as the library.
    { path: 'setup/kpi-library/categories', loadComponent: kpiCategoriesLoader },
    // F26: `performanceAreaPermissions(['setup','kpi-sets', …])` falls to the default arm and answers `templatesManage`, so no new rule.
    { path: 'setup/kpi-sets', loadComponent: kpiSetsLoader },
    { path: 'setup/kpi-sets/:kpiSetId', loadComponent: kpiSetDetailLoader },
    { path: 'setup/settings', loadComponent: settingsLoader },
    { path: 'setup/access', loadComponent: accessLoader },
  ]),
};
